import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { V2_ENDPOINT } from "@/consts";
import type { Workflow, WorkflowsApiResponse } from "@/stores/workflowsStore";
import type { PaginatedData } from "@/types/pagination";

/**
 * Filter shape mirroring backend ListFilters. Empty values mean "no filter"
 * (omitted from the request URL).
 */
// Canonical enum lists — single source of truth for the TS literal types
// AND the runtime validators in useWorkflowFilters. Adding a new
// sort/status/owner value requires touching only this block.
export const WORKFLOW_STATUS_VALUES = ["active", "draft", "paused", "archived"] as const;
export const WORKFLOW_SORT_VALUES = ["updatedAt:desc", "name:asc", "createdAt:desc", "runs:desc"] as const;
export const WORKFLOW_OWNER_VALUES = ["yours", "org", "public"] as const;

/** Full status union — includes `archived`, used by the response shape
 * (workflows can have status="archived" in the DB, just not selectable
 * in the UI). */
export type WorkflowStatus = (typeof WORKFLOW_STATUS_VALUES)[number] | "";

/** Statuses the UI can filter by. `archived` is now supported — the list
 * endpoint returns archived rows when `?status=archived` is passed
 * explicitly; default (empty) still excludes them. UI exposes the filter
 * via the status-tab overflow menu. */
export type UserFacingStatus = WorkflowStatus;

export type WorkflowSort = (typeof WORKFLOW_SORT_VALUES)[number];
export type WorkflowOwner = (typeof WORKFLOW_OWNER_VALUES)[number] | "";

export interface WorkflowFilters {
  q?: string;
  status?: UserFacingStatus;
  type?: string;
  actions?: string[];
  connectors?: string[];
  sort?: WorkflowSort;
  owner?: WorkflowOwner;
  /** Restrict to workflows the caller created (created_by = caller). */
  madeByMe?: boolean;
}

/** Page params = filters + page index/size. Used as the TanStack Query key. */
export interface WorkflowsPageParams extends WorkflowFilters {
  page: number;
  pageSize: number;
}

/**
 * Backend response for /v1/workflows/counts.
 * `total` is the non-archived tab total (active + draft + paused) — drives
 * the "All" tab which deliberately hides archived. `archived` is surfaced
 * separately so the overflow-menu Archived option can show a count badge
 * off the same query.
 */
export interface WorkflowsCounts {
  total: number;
  active: number;
  draft: number;
  paused: number;
  archived: number;
  /**
   * Distinct trigger-type and connector-type values that exist in the org's
   * workflows under the current filter context (ignoring status). Powers the
   * filter-panel dropdowns so users can pick from every option in the full
   * result set, not just the visible page.
   */
  availableTypes: string[];
  availableConnectors: string[];
}

/** Backend response for /v1/workflows/owner-counts. */
export interface OwnerCounts {
  yours: number;
  org: number;
  public: number;
}

/** Standard envelope for non-paginated endpoints. */
interface Envelope<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/** Build the URLSearchParams for a list/counts request.
 *
 * Note: `owner=yours` IS sent on default cold loads even though the URL
 * parser defaults owner to "yours" when absent. That's intentional —
 * "Yours" is the default visibility tab per product spec, and backend
 * `owner=yours` (applies `created_by = caller` + owner fallback) is
 * SEMANTICALLY DIFFERENT from omitted owner (which returns everything
 * the caller can see across their org). Flagged by aqt#1 but validated
 * as by-design. If the product ever wants "default = all org workflows",
 * only the URL parser default needs to change. */
function buildFilterParams(f: WorkflowFilters): URLSearchParams {
  const u = new URLSearchParams();
  if (f.q) u.set("q", f.q);
  if (f.status) u.set("status", f.status);
  if (f.type) u.set("type", f.type);
  if (f.sort) u.set("sort", f.sort);
  if (f.owner) u.set("owner", f.owner);
  if (f.madeByMe) u.set("madeByMe", "true");
  for (const a of f.actions ?? []) u.append("actions", a);
  for (const c of f.connectors ?? []) u.append("connectors", c);
  return u;
}

// Unwraps the standard `{ code, message, data, requestId }` envelope.
// Throws on HTTP error or non-zero `code`. `label` is used in error messages
// so callers don't have to hand-write per-endpoint strings.
async function unwrapEnvelope<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    // Try to surface the structured error body (e.g. from validateFilters'
    // 400) so debugging doesn't stop at a generic "Bad Request".
    let detail = "";
    try {
      const body = (await res.clone().json()) as { message?: string };
      if (body?.message) detail = `: ${body.message}`;
    } catch {
      // not JSON / empty body — fall back to statusText
    }
    throw new Error(`${label} failed: ${res.status} ${res.statusText}${detail}`);
  }
  const body = (await res.json()) as { code: number; message: string; data: T };
  if (body.code !== 0) {
    throw new Error(`${label} error: ${body.message}`);
  }
  // Enforce the generic contract: callers type the return as T, so a
  // null/missing data field (which would surface as a runtime null-deref
  // in consumers) must fail here with a clear message instead.
  if (body.data == null) {
    throw new Error(`${label}: missing response data`);
  }
  return body.data;
}

export async function fetchWorkflowsPage(p: WorkflowsPageParams): Promise<PaginatedData<Workflow>> {
  const u = buildFilterParams(p);
  u.set("limit", String(p.pageSize));
  u.set("offset", String(p.page * p.pageSize));
  const res = await authenticatedFetch<WorkflowsApiResponse>(`${V2_ENDPOINT}/v1/workflows?${u.toString()}`);
  return unwrapEnvelope<PaginatedData<Workflow>>(res, "workflows fetch");
}

export async function fetchWorkflowsCounts(filters: WorkflowFilters): Promise<WorkflowsCounts> {
  // `status` is the grouping dimension — the hook (useWorkflowsCounts) is
  // responsible for stripping it before calling here so cache keys stay
  // clean. Not re-stripped: one source of truth.
  const u = buildFilterParams(filters);
  const res = await authenticatedFetch<Envelope<WorkflowsCounts>>(`${V2_ENDPOINT}/v1/workflows/counts?${u.toString()}`);
  return unwrapEnvelope<WorkflowsCounts>(res, "counts fetch");
}

export async function fetchWorkflowById(workflowId: string): Promise<Workflow> {
  const res = await authenticatedFetch<Envelope<Workflow>>(`${V2_ENDPOINT}/v1/workflows/${workflowId}`);
  // unwrapEnvelope throws on null data ("workflow fetch: missing response
  // data"), so no need for a manual null-check afterward.
  return unwrapEnvelope<Workflow>(res, "workflow fetch");
}

// Variant for callers that want to treat "workflow gone" (archived, deleted,
// cross-org) as a non-error and render a placeholder instead — e.g. the
// history page's per-row name lookup. Kept distinct so the common path
// still fails loudly on unexpected 404s.
export async function fetchWorkflowByIdOrNull(workflowId: string): Promise<Workflow | null> {
  const res = await authenticatedFetch<Envelope<Workflow>>(`${V2_ENDPOINT}/v1/workflows/${workflowId}`);
  if (res.status === 404) {
    return null;
  }
  return unwrapEnvelope<Workflow>(res, "workflow fetch");
}

export async function fetchWorkflowIncomingLinks(workflowId: string): Promise<string[]> {
  const res = await authenticatedFetch<Envelope<{ ids: string[] }>>(
    `${V2_ENDPOINT}/v1/workflows/${workflowId}/incoming-links`,
  );
  const data = await unwrapEnvelope<{ ids: string[] }>(res, "incoming-links fetch");
  return data.ids ?? [];
}

export async function fetchWorkflowsOwnerCounts(): Promise<OwnerCounts> {
  const res = await authenticatedFetch<Envelope<OwnerCounts>>(`${V2_ENDPOINT}/v1/workflows/owner-counts`);
  return unwrapEnvelope<OwnerCounts>(res, "owner-counts fetch");
}
